import { Modal, Table, TableColumnsType, notification } from 'antd';
import React, { useState } from 'react';
import { Transaction } from '../../api/getLedger';
import generateKhataInvoice from '../../api/generateKhataInvoice';
import { captureError } from '../../pages/captureError';
import { track } from '../../tracking';
import moment from 'moment';
import { crossRed } from '../common/Icons';
import { AntTooltip } from '../common/AntTooltip';
import { getDoc } from '../../store/idbHandler';

const DownloadKhataInvoiceModal: React.FC<{
    isVisible: boolean;
    closeMe: () => void;
    purchasedTxns: Transaction[];
}> = ({ isVisible, closeMe, purchasedTxns }) => {
    const [isDownloadingReport, setIsDownloadingReport] = useState(false);
    const [isDownloadingReportIndex, setIsDownloadingReportIndex] = useState(-1);
    const [gstNumber, setGstNumber] = useState<string>();
    const [showGstNoInput, setShowGstNoInput] = useState(false);
    const [currGSTNo, setCurrGStNo] = useState<string>();

    const downloadInvoice = (index: number, transaction: Transaction) => {
        const doc = getDoc();
        if (!doc) return;
        track('DOWNLOAD_INVOICE', {
            txnId: transaction.transaction_id,
            id: transaction._id,
            gstNumber,
        });

        if (transaction) {
            setIsDownloadingReport(true);
            setIsDownloadingReportIndex(index);

            generateKhataInvoice(transaction.transaction_id, gstNumber)
                .then((res) => {
                    if (res.error) {
                        throw new Error(res.error);
                    } else {
                        if (res.url) {
                            window.open(res.url, '_blank');
                        } else {
                            throw new Error("Couldn't generate invoice url");
                        }
                    }
                })
                .catch((e) => {
                    track('CREDIT_INVOICE_FAILED');
                    captureError(e);
                    notification.error({
                        message: 'Failed to generate invoice!',
                        duration: 5,
                    });
                })
                .finally(() => {
                    setIsDownloadingReport(false);
                    setIsDownloadingReportIndex(-1);
                });
        }
    };

    const columns: TableColumnsType<any> = [
        {
            title: <p className="font-700 text-12 text-darwin-neutral-700 uppercase">Seq</p>,
            dataIndex: 'seqNo',
            key: 'seqNo',
            fixed: 'left',
            width: '10%',
        },
        {
            title: <p className="font-700 text-12 text-darwin-neutral-700 uppercase">Created At</p>,
            dataIndex: 'created_at',
            key: 'created_at',
        },
        {
            title: <p className="font-700 text-12 text-darwin-neutral-700 uppercase">Purpose</p>,
            dataIndex: 'purpose',
            key: 'purpose',
        },
        {
            title: <p className="font-700 text-12 text-darwin-neutral-700 uppercase">Amount</p>,
            dataIndex: 'amount',
            key: 'amount',
        },
        {
            title: <p className="font-700 text-12 text-darwin-neutral-700 uppercase">Download</p>,
            dataIndex: 'download',
            key: 'download',
            width: '15%',
        },
    ];

    const data = purchasedTxns
        ? purchasedTxns.map((t, idx) => {
              return {
                  seqNo: idx + 1,
                  created_at: (
                      <p className="text-13 font-400">{moment(t.timestamp).format('lll')}</p>
                  ),
                  purpose: <p className="text-13 font-400">Credit purchase</p>,
                  amount: <p className="text-13 font-400">{t.credit}</p>,
                  download: (
                      <div className="flex justify-center">
                          {isDownloadingReportIndex === idx ? (
                              <svg
                                  className="w-18 h-18 fill-darwin-accent-bright-blue-800 animate-spin"
                                  role="img"
                                  xmlns="http://www.w3.org/2000/svg"
                                  viewBox="0 0 512 512"
                              >
                                  <path d="M96 256c0-26.5-21.5-48-48-48S0 229.5 0 256s21.5 48 48 48S96 282.5 96 256zM108.9 60.89c-26.5 0-48.01 21.49-48.01 47.99S82.39 156.9 108.9 156.9s47.99-21.51 47.99-48.01S135.4 60.89 108.9 60.89zM108.9 355.1c-26.5 0-48.01 21.51-48.01 48.01S82.39 451.1 108.9 451.1s47.99-21.49 47.99-47.99S135.4 355.1 108.9 355.1zM256 416c-26.5 0-48 21.5-48 48S229.5 512 256 512s48-21.5 48-48S282.5 416 256 416zM464 208C437.5 208 416 229.5 416 256s21.5 48 48 48S512 282.5 512 256S490.5 208 464 208zM403.1 355.1c-26.5 0-47.99 21.51-47.99 48.01S376.6 451.1 403.1 451.1s48.01-21.49 48.01-47.99S429.6 355.1 403.1 355.1zM256 0C229.5 0 208 21.5 208 48S229.5 96 256 96s48-21.5 48-48S282.5 0 256 0z"></path>
                              </svg>
                          ) : (
                              <button
                                  onClick={() => downloadInvoice(idx, t)}
                                  disabled={isDownloadingReport}
                                  className="disabled:opacity-70 disabled:cursor-not-allowed"
                              >
                                  <svg
                                      className="w-18 h-18 fill-darwin-accent-bright-blue-800"
                                      viewBox="0 0 14 14"
                                      xmlns="http://www.w3.org/2000/svg"
                                  >
                                      <path d="M7.60938 1.23438V8.11523L10.0215 5.70312C10.25 5.44922 10.6309 5.44922 10.8848 5.70312C11.1133 5.93164 11.1133 6.3125 10.8848 6.54102L7.40625 9.99414C7.17773 10.248 6.79688 10.248 6.56836 9.99414L3.11523 6.54102C2.86133 6.3125 2.86133 5.93164 3.11523 5.70312C3.34375 5.44922 3.72461 5.44922 3.97852 5.70312L6.39062 8.11523V1.23438C6.39062 0.904297 6.64453 0.625 7 0.625C7.33008 0.625 7.60938 0.904297 7.60938 1.23438ZM3.75 8.34375L4.96875 9.5625H2.125C1.89648 9.5625 1.71875 9.76562 1.71875 9.96875V12C1.71875 12.2285 1.89648 12.4062 2.125 12.4062H11.875C12.0781 12.4062 12.2812 12.2285 12.2812 12V9.96875C12.2812 9.76562 12.0781 9.5625 11.875 9.5625H9.00586L10.2246 8.34375H11.875C12.7637 8.34375 13.5 9.08008 13.5 9.96875V12C13.5 12.9141 12.7637 13.625 11.875 13.625H2.125C1.21094 13.625 0.5 12.9141 0.5 12V9.96875C0.5 9.08008 1.21094 8.34375 2.125 8.34375H3.75ZM11.4688 10.9844C11.4688 11.3398 11.1895 11.5938 10.8594 11.5938C10.5039 11.5938 10.25 11.3398 10.25 10.9844C10.25 10.6543 10.5039 10.375 10.8594 10.375C11.1895 10.375 11.4688 10.6543 11.4688 10.9844Z" />
                                  </svg>
                              </button>
                          )}
                      </div>
                  ),
              };
          })
        : [];

    return (
        <Modal
            visible={isVisible}
            bodyStyle={{
                padding: '0px',
            }}
            footer={null}
            width="55%"
            closable={false}
            onCancel={closeMe}
        >
            <div className="flex flex-col p-16 space-y-18">
                <div className="flex items-center justify-between font-700 text-darwin-neutral-900 px-5">
                    <div className="flex gap-12 items-center">
                        <p>Credit purchases</p>
                        {gstNumber ? (
                            <div className="flex self-end gap-12 items-center">
                                <p className="text-12 text-darwin-neutral-600">
                                    GST No: {gstNumber}
                                </p>
                                <AntTooltip content="Remove GST No.">
                                    <span
                                        className="cursor-pointer"
                                        onClick={() => {
                                            setGstNumber(undefined);
                                            setCurrGStNo(undefined);
                                            setShowGstNoInput(false);
                                        }}
                                    >
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            viewBox="0 0 512 512"
                                            className="w-12 h-12 fill-darwin-neutral-600"
                                        >
                                            <path d="M48.5 224H40c-13.3 0-24-10.7-24-24V72c0-9.7 5.8-18.5 14.8-22.2s19.3-1.7 26.2 5.2L98.6 96.6c87.6-86.5 228.7-86.2 315.8 1c87.5 87.5 87.5 229.3 0 316.8s-229.3 87.5-316.8 0c-12.5-12.5-12.5-32.8 0-45.3s32.8-12.5 45.3 0c62.5 62.5 163.8 62.5 226.3 0s62.5-163.8 0-226.3c-62.2-62.2-162.7-62.5-225.3-1L185 183c6.9 6.9 8.9 17.2 5.2 26.2s-12.5 14.8-22.2 14.8H48.5z" />
                                        </svg>
                                    </span>
                                </AntTooltip>
                            </div>
                        ) : showGstNoInput ? (
                            <div className="flex">
                                <input
                                    placeholder="Enter GST No."
                                    className="px-8 rounded-l-4 border border-darwin-neutral_new-200"
                                    value={currGSTNo}
                                    onChange={(e) => setCurrGStNo(e.target.value)}
                                />
                                <div
                                    className="py-2 px-6 flex items-center justify-center rounded-r-4 bg-darwin-accent-bright-blue-800 text-white cursor-pointer"
                                    onClick={() => setGstNumber(currGSTNo)}
                                >
                                    Submit
                                </div>
                                <span
                                    onClick={() => setShowGstNoInput(false)}
                                    className="cursor-pointer ml-8 self-center"
                                >
                                    {crossRed('w-14 h-14')}
                                </span>
                            </div>
                        ) : (
                            <div
                                onClick={() => setShowGstNoInput(true)}
                                className="cursor-pointer text-12 rounded-4 py-2 px-6 border border-darwin-accent-bright-blue-800 text-darwin-accent-bright-blue-800"
                            >
                                + Add GST No
                            </div>
                        )}
                    </div>
                    <svg
                        className="w-13 h-13 fill-darwin-neutral-900 cursor-pointer"
                        viewBox="0 0 12 12"
                        xmlns="http://www.w3.org/2000/svg"
                        onClick={closeMe}
                    >
                        <path d="M10.75 9.71875C11.0312 10.0312 11.0312 10.5 10.75 10.7812C10.4375 11.0938 9.96875 11.0938 9.6875 10.7812L6 7.0625L2.28125 10.7812C1.96875 11.0938 1.5 11.0938 1.21875 10.7812C0.90625 10.5 0.90625 10.0312 1.21875 9.71875L4.9375 6L1.21875 2.28125C0.90625 1.96875 0.90625 1.5 1.21875 1.21875C1.5 0.90625 1.96875 0.90625 2.25 1.21875L6 4.96875L9.71875 1.25C10 0.9375 10.4688 0.9375 10.75 1.25C11.0625 1.53125 11.0625 2 10.75 2.3125L7.03125 6L10.75 9.71875Z" />
                    </svg>
                </div>
                <div className="w-full max-h-400 overflow-scroll border-b border-darwin-neutral-100">
                    <Table
                        columns={columns}
                        dataSource={data}
                        pagination={false}
                        bordered
                        sticky
                        locale={{
                            emptyText: (
                                <div className="flex flex-col items-center space-y-10 py-24">
                                    <svg
                                        className="ant-empty-img-simple w-64 h-40"
                                        viewBox="0 0 64 41"
                                        xmlns="http://www.w3.org/2000/svg"
                                    >
                                        <g
                                            transform="translate(0 1)"
                                            fill="none"
                                            fill-rule="evenodd"
                                        >
                                            <ellipse
                                                className="ant-empty-img-simple-ellipse"
                                                cx="32"
                                                cy="33"
                                                rx="32"
                                                ry="7"
                                            ></ellipse>
                                            <g
                                                className="ant-empty-img-simple-g"
                                                fill-rule="nonzero"
                                            >
                                                <path d="M55 12.76L44.854 1.258C44.367.474 43.656 0 42.907 0H21.093c-.749 0-1.46.474-1.947 1.257L9 12.761V22h46v-9.24z"></path>
                                                <path
                                                    d="M41.613 15.931c0-1.605.994-2.93 2.227-2.931H55v18.137C55 33.26 53.68 35 52.05 35h-40.1C10.32 35 9 33.259 9 31.137V13h11.16c1.233 0 2.227 1.323 2.227 2.928v.022c0 1.605 1.005 2.901 2.237 2.901h14.752c1.232 0 2.237-1.308 2.237-2.913v-.007z"
                                                    className="ant-empty-img-simple-path"
                                                ></path>
                                            </g>
                                        </g>
                                    </svg>
                                    <p>No purchase transaction yet</p>
                                </div>
                            ),
                        }}
                    />
                </div>
            </div>
        </Modal>
    );
};

export default DownloadKhataInvoiceModal;
